import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { LOGO_URL, headerNavLinks, PHONE_DISPLAY, PHONE_HREF } from '../data/homepage';

// Full desktop nav needs ~1190px; below this width the header collapses to the menu button.
const DESKTOP_NAV_MIN = 1280;
export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    const onScroll = () => {
      const hero = document.getElementById('top');
      const limit = hero ? hero.offsetTop + hero.offsetHeight - 120 : 400;
      setAtTop(window.scrollY < limit);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (!open) return;
    firstLinkRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onResize = () => {
      if (window.innerWidth >= DESKTOP_NAV_MIN) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);
  return <>
      {/* Non-sticky service statement */}
      <div className="bg-mist text-ink" data-id="element-201" data-mp="25hypp1kh96x1">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-3 px-5 py-1 md:gap-6 md:px-8" data-id="element-202" data-mp="25jrvken9nn3z">
          <p className="min-w-0 text-left text-[13px] font-semibold leading-[1.45]" data-id="element-203" data-mp="182kurgk9rsiz">
            <span className="md:hidden" data-id="element-204" data-mp="1d0cw116t0qzu">Custom websites, SEO &amp; automation.</span>
            <span className="hidden md:inline" data-id="element-205" data-mp="14zx46lv06ict">
              Custom websites, ecommerce, SEO, Google Ads support, lead generation, integrations, hosting, and ongoing
              website management.
            </span>
          </p>
          <a href={PHONE_HREF} aria-label={`Call WebsiteForge at ${PHONE_DISPLAY}`} className="inline-flex min-h-6 shrink-0 items-center justify-end gap-2 whitespace-nowrap text-right text-[14px] font-extrabold hover:underline md:text-[16px]" data-id="element-206" data-mp="1et443r6z565i">
            <PhoneIcon className="hidden h-4 w-4 sm:block" aria-hidden data-id="element-207" data-mp="h4u39fixcofz" />
            {PHONE_DISPLAY}
          </a>
        </div>
      </div>
      <header className="on-dark sticky top-0 z-40 bg-ink text-white" data-id="element-208" data-mp="r1gmjzvlr7fw">
        <div className="mx-auto flex h-[72px] max-w-[1280px] items-center justify-between gap-6 px-5 md:px-8 xl:h-20" data-id="element-209" data-mp="r05l46pmqbry">
          <a href="#top" className="flex shrink-0 items-center gap-4" aria-label="WebsiteForge, business solutions since 2002, back to top" data-id="element-210" data-mp="22gm2i5kqv2hg">
            <img src={LOGO_URL} alt="" className="h-10 w-auto xl:h-11" width={196} height={44} data-id="element-211" data-mp="29z3f4knx2xen" />
            <span className="hidden h-9 w-px bg-white/25 sm:block" aria-hidden data-id="element-212" data-mp="2f099emx86sqa" />
            <span className="hidden text-[11px] font-bold uppercase leading-[1.35] text-white/75 sm:block" aria-hidden data-id="element-213" data-mp="20zyhdgk6j83j">
              Business solutions
              <br data-id="element-214" data-mp="251blnm7mj38c" />
              Since 2002
            </span>
          </a>

          <nav aria-label="Primary" className="hidden xl:block" data-id="element-215" data-mp="2srxjylrtt955">
            <ul className="flex items-center gap-7" data-id="element-216" data-mp="2xq8uyztuxpoy">
              {headerNavLinks.map(link => {
              const active = link.href === '#top' && atTop;
              return <li key={link.href} data-id="element-217" data-mp="2nzo11pp4p9cd">
                    <a href={link.href} aria-current={active ? 'location' : undefined} className={`relative whitespace-nowrap py-2 text-[15px] font-semibold transition-colors duration-150 ease-out hover:text-white ${active ? 'text-white' : 'text-white/80'}`} data-id="element-218" data-mp="2nyl718wieqs8">
                      
                      {link.label}
                      <span aria-hidden className={`absolute inset-x-0 -bottom-0.5 h-0.5 rounded-full bg-brand-orange transition-opacity duration-150 ease-out ${active ? 'opacity-100' : 'opacity-0'}`} data-id="element-219" data-mp="1nuvh4p0k5vfq" />
                      
                    </a>
                  </li>;
            })}
            </ul>
          </nav>

          <div className="flex items-center gap-3" data-id="element-220" data-mp="21sk4c3i3leny">
            <a href="#contact" className="group hidden items-center gap-2 whitespace-nowrap rounded-md bg-brand-orange px-5 py-3 text-[15px] font-bold text-ink transition-colors duration-150 ease-out hover:bg-brand-orangeHover sm:inline-flex" data-id="element-221" data-mp="21r0o2cwfz3m6">
              
              Discuss Your Project
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden data-id="element-222" data-mp="2pg1rflqf3okl" />
              
            </a>
            <button ref={toggleRef} type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-md text-white transition-colors duration-150 ease-out hover:bg-white/10 xl:hidden" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Close menu' : 'Open menu'} onClick={() => setOpen(v => !v)} data-id="element-223" data-mp="h0pdnad48ws6">
              
              {open ? <XIcon className="h-6 w-6" aria-hidden data-id="element-224" data-mp="14p8ljos977jq" /> : <MenuIcon className="h-6 w-6" aria-hidden data-id="element-225" data-mp="14qa8aqimtiha" />}
            </button>
          </div>
        </div>

        <AnimatePresence data-id="element-226" data-mp="18rlroeawx7h1">
          {open && <motion.nav id="mobile-menu" aria-label="Mobile" initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: reduceMotion ? 0 : 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="absolute inset-x-0 top-full border-t border-ink-line bg-ink shadow-[0_12px_24px_rgba(0,0,0,0.25)] xl:hidden" data-id="element-227" data-mp="1dpx0clph1bxo">
            
              <ul className="mx-auto max-w-[1280px] px-5 py-4 md:px-8" data-id="element-228" data-mp="3oipsp1guoouw">
                {headerNavLinks.map((link, i) => {
              const active = link.href === '#top' && atTop;
              return <li key={link.href} className="border-b border-ink-line last:border-b-0" data-id="element-229" data-mp="3tfzp512nq0s8" data-mpi={i}>
                      <a ref={i === 0 ? firstLinkRef : undefined} href={link.href} aria-current={active ? 'location' : undefined} onClick={() => setOpen(false)} className={`flex items-center gap-3 py-4 text-[17px] font-semibold ${active ? 'text-white' : 'text-white/85'}`} data-id="element-230" data-mp="bacylpnu0i91">
                      
                        {active && <span className="h-4 w-0.5 rounded-full bg-brand-orange" aria-hidden data-id="element-231" data-mp="nk2lf305s2kg" />}
                        {link.label}
                      </a>
                    </li>;
            })}
                <li className="pt-4" data-id="element-232" data-mp="dvccf76pbeby">
                  <a href="#contact" onClick={() => setOpen(false)} className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-orange px-5 py-3.5 text-[16px] font-bold text-ink" data-id="element-233" data-mp="du9mp2l47hxm">
                  
                    Discuss Your Project
                    <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-234" data-mp="9uio4cpcjxij" />
                  </a>
                </li>
              </ul>
            </motion.nav>}
        </AnimatePresence>
      </header>
    </>;
}